const FINALIZE_DYNAMIC_STEP = "finalize_dynamic_step";

const FINALIZE_OK_STATUSES = new Set(["ok", "success", "passed", "completed"]);

function normalizeTraceStatus(raw) {
    if (raw == null || String(raw).trim() === "") return null;
    return String(raw).trim().toLowerCase();
}

/** Parsed `content` on finalize tool_response (transport status may still be "ok"). */
function parseFinalizeContentStatus(resp) {
    const raw = resp?.content;
    if (raw == null || raw === "") return null;
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (parsed && typeof parsed === "object") return normalizeTraceStatus(parsed.status);
    } catch {
        /* non-JSON content */
    }
    return null;
}

/**
 * finalize_dynamic_step: tool_call args.status and response content reflect pass/fail;
 * tool_response.status is often "ok" (delivery) even when args.status is "failed".
 */
function resolveFinalizeResponseStatus(call, resp) {
    const finalizeArgs = call?.args && typeof call.args === "object" ? call.args : {};
    const callStatus = normalizeTraceStatus(finalizeArgs.status);
    const contentStatus = parseFinalizeContentStatus(resp);
    const transportStatus = normalizeTraceStatus(resp?.status);

    for (const s of [callStatus, contentStatus, transportStatus]) {
        if (s && !FINALIZE_OK_STATUSES.has(s)) return s;
    }
    for (const s of [callStatus, contentStatus, transportStatus]) {
        if (s && FINALIZE_OK_STATUSES.has(s)) return "ok";
    }
    return "ok";
}

function formatToolCallEvent(event) {
    if (!event || event.type !== "tool_call") return "";
    const tool = event.tool || "";
    const args = event.args && typeof event.args === "object" ? event.args : {};
    if (tool === FINALIZE_DYNAMIC_STEP) {
        if (args.summary != null) return String(args.summary);
        return "";
    }
    const lines = [];
    if (args.element != null && String(args.element).trim() !== "") {
        lines.push(`Element: ${args.element}`);
    }
    if (args.ref != null && String(args.ref).trim() !== "") {
        lines.push(`Ref: ${args.ref}`);
    }
    if (args.text != null && String(args.text).trim() !== "") {
        lines.push(`Text: ${args.text}`);
    }
    if (lines.length > 0) return lines.join("\n");
    try {
        return JSON.stringify(args, null, 2);
    } catch {
        return "";
    }
}

function formatToolResponseEvent(event) {
    if (!event || event.type !== "tool_response") return "";
    const tool = event.tool || "";
    const raw = event.content;
    if (tool === FINALIZE_DYNAMIC_STEP) {
        if (raw == null || raw === "") return "";
        const s = typeof raw === "string" ? raw : String(raw);
        try {
            const parsed = JSON.parse(s);
            return typeof parsed === "object" && parsed !== null
                ? JSON.stringify(parsed, null, 2)
                : String(parsed);
        } catch {
            return s;
        }
    }
    if (raw == null) return "";
    return typeof raw === "string" ? raw : String(raw);
}

/** Split formatted tool-call text into lines; `label` is through first `:` for `.toolTraceKey` in UI. */
function toolCallBodyDisplayLines(formattedBody) {
    if (!formattedBody || !String(formattedBody).trim()) return [];
    return String(formattedBody).split("\n").map((line) => {
        const idx = line.indexOf(":");
        if (idx > 0) return { label: line.slice(0, idx + 1), value: line.slice(idx + 1) };
        return { label: null, value: line };
    });
}

/** Maps replay `stepWiseDynamicResults` into `StepAgentResultTrace` turn rows. */
export function mapStepWiseDynamicResultsToTraceTurns(events) {
    if (!Array.isArray(events) || events.length === 0) return [];

    const groups = new Map();
    for (const e of events) {
        if (!e || typeof e !== "object") continue;
        const gRaw = e.group;
        const g = typeof gRaw === "number" && Number.isFinite(gRaw) ? gRaw : Number(gRaw);
        const key = Number.isFinite(g) ? g : 0;
        if (!groups.has(key)) {
            groups.set(key, { toolCalls: [], toolResponses: [] });
        }
        const b = groups.get(key);
        const t = e.type;
        if (t === "thinking") {
            /* reserved — empty thinking in turns until payload is defined */
        } else if (t === "tool_call") b.toolCalls.push(e);
        else if (t === "tool_response") b.toolResponses.push(e);
    }

    const byCallIndex = (list) => {
        const m = new Map();
        for (const x of list) {
            const ci = x.callIndex;
            const i = typeof ci === "number" && Number.isFinite(ci) ? ci : Number(ci);
            const key = Number.isFinite(i) ? i : 0;
            m.set(key, x);
        }
        return m;
    };

    const sortedGroupKeys = [...groups.keys()].sort((a, b) => a - b);
    const turns = [];

    for (const g of sortedGroupKeys) {
        const { toolCalls, toolResponses } = groups.get(g);
        const callsMap = byCallIndex(toolCalls);
        const respsMap = byCallIndex(toolResponses);
        const callIndices = new Set([...callsMap.keys(), ...respsMap.keys()]);
        const sortedIdx = [...callIndices].sort((a, b) => a - b);

        for (const ci of sortedIdx) {
            const call = callsMap.get(ci);
            const resp = respsMap.get(ci);
            if (!call && !resp) continue;
            if (!call && resp?.tool === FINALIZE_DYNAMIC_STEP) continue;

            const tool = call?.tool || resp?.tool || "—";
            // if (call?.tool === FINALIZE_DYNAMIC_STEP) {
            //     const args = call.args && typeof call.args === "object" ? call.args : {};
            //     const st = String(args.status ?? "").trim().toLowerCase();
            //     if (st !== "completed") continue;
            // }

            const finalizeSummaryOnly = call?.tool === FINALIZE_DYNAMIC_STEP;
            const finalizeArgs = call?.args && typeof call.args === "object" ? call.args : {};
            const finalizeSummaryBody = finalizeSummaryOnly
                ? (finalizeArgs.summary != null ? String(finalizeArgs.summary) : "")
                : "";

            const rawStatus = resp?.status;
            let responseStatus =
                rawStatus != null && String(rawStatus).trim() !== ""
                    ? String(rawStatus).trim().toLowerCase()
                    : "ok";
            if (finalizeSummaryOnly) {
                responseStatus = resolveFinalizeResponseStatus(call, resp);
            }
            const toolCallBody = finalizeSummaryOnly ? "" : formatToolCallEvent(call);
            turns.push({
                id: `swd-g${g}-c${ci}`,
                toolName: tool,
                thinking: "",
                toolCallBody,
                toolCallBodyLines: finalizeSummaryOnly ? [] : toolCallBodyDisplayLines(toolCallBody),
                responseBody: finalizeSummaryOnly ? "" : formatToolResponseEvent(resp),
                responseStatus,
                hideResponseRow: finalizeSummaryOnly,
                finalizeSummaryOnly,
                finalizeSummaryBody,
            });
        }
    }

    const lastFinalizeIdx = turns.findLastIndex(t => t.finalizeSummaryOnly);
    if (lastFinalizeIdx >= 0) {
        return turns.filter((t, i) => !t.finalizeSummaryOnly || i === lastFinalizeIdx);
    }

    return turns;
}
